import React, { useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import { Formik } from 'formik';
import * as yup from 'yup';
import { Modal } from 'react-bootstrap';

import close from '../../assets/close.png';
import styles from './reset-password.module.scss';
import { resetPasswordConfirm, setPassword } from '../../services/auth.api';
import { ResetPasswordConfirmRequest } from '../../services/models/resetPasswordConfirm';
import { SetPasswordRequest } from '../../services/models/setPassword';
import ButtonLoader from '../common/ButtonLoader';
import { StatusType } from '../../types';
import StatusMessage from '../common/StatusMessage';
import { commonPasswords } from '../../commonPasswords';

interface ResetPasswordConfirmProps {
  isSocialLogin?: boolean;
  onSuccess?: () => void;
}

interface ResetPasswordConfirmModalProps {
  closeModal: () => void;
  show: boolean;
  setShowToast: (showToast: boolean) => void;
}

const ResetPasswordConfirm: React.FC<ResetPasswordConfirmProps> = props => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ message: '', type: StatusType.none });
  //TODO - Match password strength as current
  const schema = yup.object({
    new_password1: yup
      .string()
      .required()
      .min(8, 'Password must be at least 8 characters')
      .test(
        'all-numbers-pwd',
        'Password must contain at least one letter',
        function(value: string) {
          return value?.match(/^[0-9]+$/) === null;
        },
      )
      .test('common-pwd', 'Password is too common', function(value: string) {
        return commonPasswords.indexOf(value) === -1;
      })
      .label('Password'),
    new_password2: yup
      .string()
      .required()
      .min(8, 'Password must be at least 8 characters')
      .oneOf([yup.ref('new_password1')], 'Passwords do not match')
      .label('Password'),
  });

  const onSubmit = values => {
    setLoading(true);
    //TODO - Check token first to see if this is valid or not
    let api = null;
    if (props.isSocialLogin) {
      const payload: SetPasswordRequest = {
        password: values.new_password1,
      };
      api = setPassword(payload);
    } else {
      const payload: ResetPasswordConfirmRequest = {
        token: props.token,
        uid: props.uid,
        new_password1: values.new_password1,
        new_password2: values.new_password2,
      };
      api = resetPasswordConfirm(payload);
    }
    api
      .then(() => {
        setLoading(false);
        if (props.isSocialLogin) {
          props.onSuccess && props.onSuccess();
        } else {
          setStatus({
            message: 'Password updated successfully.',
            type: StatusType.success,
          });
        }
      })
      .catch(e => {
        setLoading(false);
        const errStatus = {
          type: StatusType.error,
        };
        if (props.isSocialLogin) {
          errStatus.message = e;
        } else {
          errStatus.message = 'This reset password link is used/expired.';
          errStatus.link = '/reset-password';
          errStatus.linkText = 'Request another link';
        }
        setStatus(errStatus);
      });
  };

  const containerClasses = [styles.container];

  if (!props.isSocialLogin) {
    containerClasses.push(styles.containerPaddings);
  }

  return (
    <div className={containerClasses.join(' ')}>
      <div className={styles.innerContainer}>
        <Row className={props.isSocialLogin ? '' : 'mgn-top-30'}>
          <Col className="text-center">
            <h2>
              {props.isSocialLogin ? 'Reset Password' : 'Enter a new password'}
            </h2>
            <p className="mgn-top-30">
              Please enter your new password twice so we can verify you typed it
              in correctly.
            </p>
          </Col>
        </Row>
        <Formik
          validationSchema={schema}
          onSubmit={onSubmit}
          initialValues={{
            new_password1: '',
            new_password2: '',
          }}
        >
          {({ handleSubmit, handleChange, values, touched, errors }) => (
            <Row className="mgn-top-30">
              <Col>
                <Form noValidate={true} onSubmit={handleSubmit}>
                  <Form.Group controlId="formNewPassword1">
                    <Form.Label>Choose a new password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder=""
                      name="new_password1"
                      value={values.new_password1}
                      onChange={handleChange}
                      isInvalid={
                        touched.new_password1 && !!errors.new_password1
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.new_password1}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group controlId="formNewPassword2">
                    <Form.Label>Confirm new password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder=""
                      name="new_password2"
                      value={values.new_password2}
                      onChange={handleChange}
                      isInvalid={
                        touched.new_password2 && !!errors.new_password2
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.new_password2}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <div className="text-center cust-modal-footer mgn-top-20">
                    <ButtonLoader
                      className="btn-def"
                      type="submit"
                      text={
                        props.isSocialLogin
                          ? 'Reset Password'
                          : 'Change Password'
                      }
                      loading={loading}
                    />
                  </div>
                </Form>
              </Col>
            </Row>
          )}
        </Formik>
        <StatusMessage
          message={status.message}
          type={status.type}
          link={status?.link}
          linkText={status?.linkText}
        />
      </div>
    </div>
  );
};

export const ResetPasswordConfirmModal: React.FC<ResetPasswordConfirmModalProps> = props => {
  const { closeModal, show, setShowToast } = props;
  return (
    <>
      <Modal show={show} onHide={closeModal}>
        <Modal.Body>
          <div className="cust-modal-content-wrapper">
            <div className="modal-close pos-rel">
              <img onClick={e => closeModal()} src={close} alt="" />
            </div>
            <ResetPasswordConfirm
              isSocialLogin={true}
              onSuccess={() => {
                setShowToast(true);
                closeModal();
              }}
            />
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ResetPasswordConfirm;
